import { Button, Card } from '@parrot-software/design-system';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { InfoNote } from '@identity/presentation/components/base';
import { PROFILE_CHANGE_PASSWORD_ROUTE } from '@identity/presentation/constants';
import { formatChangedAt } from '@identity/presentation/utils';

export type ProfilePasswordCardProps = {
  /** ISO-8601 instant of the last change. Absent hides the whole line. */
  passwordChangedAt?: string;
};

/**
 * The password card of Mi perfil: the way out to the change, when it was last changed, and the note
 * about what happens if it is forgotten.
 *
 * The line with the date disappears when the backend sent none, instead of showing a placeholder: an
 * account whose password was never changed has no date to name.
 *
 * @param {ProfilePasswordCardProps} props - Component properties
 */
export const ProfilePasswordCard = ({ passwordChangedAt }: ProfilePasswordCardProps) => {
  const { t } = useTranslation('identity');
  const history = useHistory();
  const lastUpdated = formatChangedAt(passwordChangedAt);

  return (
    <Card
      className="flex w-full flex-col gap-6 border-0 bg-transparent p-0 font-sans shadow-none md:border md:bg-card md:p-6 md:shadow-xs"
      data-testid="profile-password-card"
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-foreground">{t('profile.password.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('profile.password.description')}</p>
      </div>

      <div className="flex flex-col items-start gap-1">
        <Button
          className="h-auto p-0"
          onClick={() => history.push(PROFILE_CHANGE_PASSWORD_ROUTE)}
          type="button"
          variant="link"
        >
          {t('profile.password.action')}
        </Button>
        {lastUpdated && (
          <p className="text-sm text-muted-foreground" data-testid="profile-password-last-updated">
            {t('profile.password.lastUpdated', { date: lastUpdated })}
          </p>
        )}
      </div>

      <InfoNote>
        <Trans components={{ strong: <strong /> }} i18nKey="identity:profile.password.note" />
      </InfoNote>
    </Card>
  );
};
